import { createContext, use, useEffect, useMemo, useState } from "react";
import type { JSX, ReactNode } from "react";
import { I18nProvider } from "../i18n/I18nContext";
import { isLocale } from "../i18n/translate";
import type { Locale } from "../i18n/translate";
import type { PreferenceStore } from "./PreferenceStore";

/** Colour scheme preference; `system` follows the operating system. */
export type ThemePreference = "system" | "light" | "dark";

/** Value of the preferences context. */
export interface PreferencesContextValue {
  /** Current locale. */
  readonly locale: Locale;
  /** Current theme preference. */
  readonly theme: ThemePreference;
  /** Changes the locale. */
  readonly setLocale: (locale: Locale) => void;
  /** Changes the theme preference. */
  readonly setTheme: (theme: ThemePreference) => void;
  /**
   * Uses a language of the employer (T-108) unless the person chose one on this device; the
   * default is not stored as a choice.
   */
  readonly applyDefaultLocale: (locale: Locale) => void;
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

/** Key of the language chosen on this device in the preference store. */
export const localePreferenceKey = "timetracking.locale";
const themeKey = "timetracking.theme";

/**
 * Checks whether a value is a theme preference.
 *
 * @param value - The value.
 * @returns `true` for a valid preference.
 */
export function isThemePreference(value: unknown): value is ThemePreference {
  const valid = value === "system" || value === "light" || value === "dark";

  return valid;
}

/** Properties of {@link PreferencesProvider}. */
export interface PreferencesProviderProps {
  /** Store the preferences are kept in. */
  readonly store: PreferenceStore;
  /** Document whose root element receives `lang` and `data-theme`. */
  readonly document: Document;
  /** Child elements. */
  readonly children: ReactNode;
}

/**
 * Provides language and theme preferences and applies them to the document.
 * German is the default language (spec 11.2).
 *
 * @param props - Component properties.
 * @returns The provider element.
 */
export function PreferencesProvider(props: PreferencesProviderProps): JSX.Element {
  const { store, document } = props;
  const [locale, setLocaleState] = useState<Locale>(() => {
    const stored = store.read(localePreferenceKey);
    const initial: Locale = isLocale(stored) ? stored : "de";

    return initial;
  });
  const [theme, setThemeState] = useState<ThemePreference>(() => {
    const stored = store.read(themeKey);
    const initial: ThemePreference = isThemePreference(stored) ? stored : "system";

    return initial;
  });

  useEffect(() => {
    document.documentElement.setAttribute("lang", locale);
  }, [document, locale]);

  useEffect(() => {
    if (theme === "system") {
      document.documentElement.removeAttribute("data-theme");
    } else {
      document.documentElement.setAttribute("data-theme", theme);
    }
  }, [document, theme]);

  const value = useMemo<PreferencesContextValue>(
    () => ({
      locale,
      theme,
      setLocale: (next: Locale): void => {
        store.write(localePreferenceKey, next);
        setLocaleState(next);
      },
      setTheme: (next: ThemePreference): void => {
        store.write(themeKey, next);
        setThemeState(next);
      },
      applyDefaultLocale: (next: Locale): void => {
        if (!isLocale(store.read(localePreferenceKey))) {
          setLocaleState(next);
        }
      },
    }),
    [locale, theme, store],
  );
  const provider = (
    <PreferencesContext value={value}>
      <I18nProvider locale={locale}>{props.children}</I18nProvider>
    </PreferencesContext>
  );

  return provider;
}

/**
 * Returns the preferences context.
 *
 * @returns The context value.
 * @throws Error if no {@link PreferencesProvider} is present.
 */
export function usePreferences(): PreferencesContextValue {
  const value = use(PreferencesContext);

  if (value === null) {
    throw new Error("usePreferences must be used inside a PreferencesProvider.");
  }

  return value;
}
